/* eslint-disable react/prop-types */
import React, { memo, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  Controller,
  RegisterOptions,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { CancelButton } from 'src/components/atoms'
import { RegexTrailingZerosAndDot } from 'src/utils/regularExpression'
import {
  getCursorPositionFromNonCommaCount,
  getHalfWidthNumericString,
  getValueWithComma,
} from './functions'
import styles from './styles.module.scss'

export type TextFieldWithCommaProps = {
  name: string
  defaultValue?: number
  placeholder?: string
  options: RegisterOptions
  onKeyPress: () => void
} & React.StyledProps

const TextFieldWithComma: React.StyledFC<TextFieldWithCommaProps> = memo(
  ({ className, name, defaultValue, placeholder, options, onKeyPress }) => {
    const { register, setValue, control, clearErrors } = useFormContext()
    const watchedValue = useWatch({ name, defaultValue })
    const [valueWithComma, setValueWithComma] = useState<string>(
      defaultValue?.toLocaleString() ?? '0'
    )
    const [cursorPosition, setCursorPosition] = useState<number | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    // NOTE: 手入力(onChange)由来の変更かどうかを識別するフラグ。
    // Tab移動時の全選択を、カーソル補正useEffectが打ち消さないようにするために使う。
    const isUserTypingRef = useRef(false)
    // NOTE: マウス操作によるフォーカスかどうかを識別するフラグ。
    // マウスクリックは必ず mousedown → focus の順で発火するため、
    // mousedown が直前にあった＝マウス由来のフォーカスと判定できる。
    // これによりモジュールスコープの共有状態を持たずにインスタンス内で完結でき、
    // 「Tabで別要素へ抜けたあとのマウスクリックで誤って全選択される」問題を防ぐ。
    const isMouseFocusingRef = useRef(false)

    const handleClickClear = () => {
      setValueWithComma('')
      setValue(name, '')
      clearErrors(name)
    }

    const handleCursorChange = () => {
      if (inputRef.current) {
        setCursorPosition(inputRef.current.selectionStart)
      }
    }

    // NOTE: マウス押下を記録する。直後のfocusをマウス由来と判定するために使う。
    const handleMouseDown = () => {
      isMouseFocusingRef.current = true
    }

    // NOTE: キーボード操作（Tab移動）でフォーカスした際に値を全選択する。
    // そのまま打鍵すれば既存値が丸ごと置換される（先頭に数字が挿入される現象を防ぐ）。
    // マウスクリック由来のフォーカスでは全選択せず、クリック位置のキャレットを維持する
    // （OCR金額の一部の桁だけをクリックして修正する操作を妨げないため）。
    //
    // 全選択とキャレット末尾はHTML仕様上同時には表現できない（選択範囲とキャレットは
    // 同じ selectionStart/End で表される）。そこで setSelectionRange の第3引数で
    // 選択方向を 'forward' にし、キャレット（anchor）を末尾(selectionEnd)側に置く。
    // これにより全選択しつつ、矢印キー等で全選択が解除されたときはキャレットが末尾に来る。
    const handleFocus = () => {
      // NOTE: 手入力補正フラグが残っていてもフォーカス時点でリセットする。
      // 整形で除去される文字を打鍵するとReactがstate更新をbail outし、補正useEffectが
      // 発火せずフラグがtrueのまま残ることがある。その状態でTabで戻ると、rAFの全選択が
      // 直後のTab keyup由来の補正(setSelectionRange)に潰されて全選択が効かなくなるのを防ぐ。
      isUserTypingRef.current = false
      // マウス由来のフォーカスなら全選択しない。フラグは消費してリセットする。
      if (isMouseFocusingRef.current) {
        isMouseFocusingRef.current = false
        return
      }
      // NOTE: Tab移動時は「前フィールドのblur(removeTrailingChar)による再レンダリング → 新フィールドのfocus」
      // の順序で、focus直後の同期的なselectionがその後の再レンダリングで失われることがある
      // （「何回か操作すると前のカーソル位置に戻る」現象）。
      // requestAnimationFrameで次フレームに全選択を実行し、再レンダリング後に確実に反映させる。
      requestAnimationFrame(() => {
        if (!inputRef.current) return
        // NOTE: Tabフォーカスの直後（rAF発火前）に同じ欄をクリックした場合は mousedown が発火し
        // isMouseFocusingRef が true になる。ここで全選択するとクリック位置のキャレットを
        // 上書きしてしまう（OCR金額の部分修正を妨げる）ため、その場合は全選択しない。
        if (isMouseFocusingRef.current) {
          isMouseFocusingRef.current = false
          return
        }
        const { length } = inputRef.current.value
        inputRef.current.setSelectionRange(0, length, 'forward')
      })
    }

    const handleInputValueChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ): number | '' => {
      const inputValue = e.target.value
      const inputValueWithoutComma = getHalfWidthNumericString(inputValue)

      // NOTE: 空入力（クリア）のときはフラグを立てずに抜ける。
      // ここより前でフラグを立てると、クリア後もフラグが残り、
      // その後の外部(watchedValue)更新で不要なカーソル補正が走ってしまう。
      if (!inputValueWithoutComma) return ''

      // NOTE: 手入力による変更なので、カーソル補正useEffectを1回だけ動かすためにフラグを立てる
      isUserTypingRef.current = true

      const inputValueWithComma = getValueWithComma(inputValueWithoutComma)
      // NOTE: カーソル左の「整形後に残る文字数」を求め、整形後の文字列でその個数目の
      // 直後にカーソルを復元する。カンマの増減量に依存しないため、全置換・中間挿入・
      // 桁区切りまたぎでもずれない。
      // 数える基準は getHalfWidthNumericString(整形) で揃える。生入力のまま数えると、
      // 整形で除去される文字（数字以外・小数3桁目など）が混ざったとき個数が水増しされ、
      // カーソルが右へ飛んでしまう（例: 1,000 の「1」直後で x を打つと 10,500 になる）。
      const nonCommaCount = getHalfWidthNumericString(
        inputValue.slice(0, e.target.selectionStart ?? inputValue.length)
      ).length
      const adjustCursorPosition = getCursorPositionFromNonCommaCount(
        inputValueWithComma,
        nonCommaCount
      )

      setCursorPosition(adjustCursorPosition)
      setValueWithComma(inputValueWithComma)
      return Number(inputValueWithoutComma)
    }

    // NOTE: フォーカスから離れたときに、整数末尾の「.」または、小数末尾の0が残っている場合は削除する
    const removeTrailingChar = () => {
      const newValue = valueWithComma.replace(RegexTrailingZerosAndDot, '$1')
      if (newValue !== valueWithComma) {
        setValueWithComma(newValue)
        setValue(name, Number(newValue.replace(/,/g, '')) || '')
      }
    }

    // NOTE: 初期値をformに適用させる
    useEffect(() => {
      setValue(name, defaultValue ?? 0)
    }, [name, setValue, defaultValue])

    // NOTE: component外部からformの値を変更する
    // ユーザーの手入力と区別するために、外部からの変更の際はformの値を文字列で扱う
    useEffect(() => {
      if (watchedValue === '') {
        setValueWithComma('')
        return
      }
      if (typeof watchedValue === 'string') {
        const numValue = Number(watchedValue.replace(/,/g, ''))
        setValueWithComma(numValue.toLocaleString() ?? '0')
        setValue(name, numValue)
      }
    }, [watchedValue, name, setValue])

    useEffect(() => {
      // NOTE: 手入力(onChange)由来のときだけカーソルを補正する。
      // フォーカス直後(onFocusのselect())の全選択をこの補正で打ち消さないためのガード。
      if (!isUserTypingRef.current) return
      if (cursorPosition !== null && inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
      }
      // 1回の手入力補正で消費する
      isUserTypingRef.current = false
    }, [valueWithComma, cursorPosition])

    return (
      <Controller
        name={name}
        control={control}
        rules={options}
        render={({ field }) => (
          <div className={clsx(className, styles.textField)}>
            <input
              {...register(name)}
              ref={inputRef}
              className={styles.input}
              name={name}
              type="text"
              value={valueWithComma}
              placeholder={placeholder}
              onBlur={() => {
                field.onBlur()
                removeTrailingChar()
                // NOTE: フォーカス済みの欄を再クリックすると mousedown だけが発火し focus は発火しないため、
                // isMouseFocusingRef が true のまま残る。次にこの欄へTabで戻ったとき全選択が誤ってスキップされる。
                // 欄から離れる際に必ずリセットし、フラグが残らないようにする。
                isMouseFocusingRef.current = false
              }}
              onFocus={handleFocus}
              onMouseDown={handleMouseDown}
              onKeyUp={handleCursorChange}
              onChange={(e) => {
                field.onChange(handleInputValueChange(e))
                onKeyPress()
              }}
            />
            {Boolean(watchedValue ?? defaultValue) && (
              <CancelButton
                className={styles.cancelButton}
                onClick={handleClickClear}
              />
            )}
          </div>
        )}
      />
    )
  }
)
TextFieldWithComma.displayName = 'TextFieldWithComma'

export default TextFieldWithComma
